










































































import { Component, Prop, Vue } from 'vue-property-decorator';
import Entry from '../models/Entry';
import EntryGroupItem from '../models/EntryGroupItem';
import VEntrySearch, { VEntrySearchResult } from './VEntrySearch.vue';
import VEntryList from './VEntryList.vue';
import VEntryListGroup from './VEntryListGroup.vue';
import VIcon from './VIcon.vue';

const STATUS_DEFAULT = 'default';
const STATUS_CREATE = 'create';
const STATUS_EDIT = 'edit';

const MAX_ELEMENTS_COUNT = 10;

@Component({
  components: {
    VEntrySearch,
    VEntryList,
    VEntryListGroup,
    VIcon
  }
})
export default class ComparisonGroupEditor extends Vue {
  @Prop({ default: null }) readonly payload? : any;
  @Prop({ default: MAX_ELEMENTS_COUNT }) readonly maxElements? : number;
  @Prop({ default: () => { return Promise.resolve([]); } }) readonly loadGroups!: (payload: any|null) => Promise<EntryGroupItem[]>;
  @Prop({ default: () => { return Promise.resolve({}); } }) readonly createGroup!: (group: EntryGroupItem) => Promise<EntryGroupItem>;
  @Prop({ default: (entry: Entry, group: EntryGroupItem) => { return Promise.resolve(); } }) readonly addEntryToGroup!: (entry: Entry, group: EntryGroupItem) => Promise<void>;
  @Prop({ default: (entry: Entry, group: EntryGroupItem) => { return Promise.resolve(); } }) readonly removeEntryFromGroup!: (entry: Entry, group: EntryGroupItem) => Promise<void>;

  status: string = STATUS_DEFAULT;
  loading = false;
  current: EntryGroupItem = { id: '', title: '', entries: [] };

  selected: EntryGroupItem|null = null;

  groups: EntryGroupItem[] = [];

  searchResult: VEntrySearchResult = {
    items: [],
    total: 0
  };

  get isCreating(): boolean {
    return this.status === STATUS_CREATE;
  }

  get isEditing(): boolean {
    return this.status === STATUS_EDIT;
  }

  get isSelected(): boolean {
    return this.selected !== null;
  }

  get canAddElements(): boolean {
    if (!this.selected) {
      return false;
    } else {
      if (!this.selected.entries) {
        return false;
      } else {
        return this.selected.entries.length < (this.maxElements || MAX_ELEMENTS_COUNT);
      }
    }
  }

  created() {
    this.load();
  }

  startCreating() {
    this.status = STATUS_CREATE;

    this.$nextTick(() => {
      (this.$refs.name as HTMLInputElement).focus();
    });
  }

  stopCreating() {
    this.status = STATUS_DEFAULT;
    this.current = { id: '', title: '', entries: [] };
  }

  startEditing() {
    this.status = STATUS_EDIT;

    this.$nextTick(() => {
      (this.$refs.search as VEntrySearch).focus();
    });
  }

  stopEditing() {
    this.status = STATUS_DEFAULT;
    this.clearSearchResult();
  }

  select(group: EntryGroupItem) {
    this.status = STATUS_DEFAULT;
    this.selected = group;

    this.emitSelected();
  }

  public selectFirst() {
    if (this.groups.length) {
      this.selected = this.groups[0];
      this.emitSelected();
    }
  }

  public selectById(id: string) {
    const group = this.groups.find((group: EntryGroupItem) => group.id === id);
    if (group) {
      this.select(group);
    }
  }

  protected async load() {
    this.loading = true;
    this.groups = await this.loadGroups(this.payload);
    this.sortGroups();
    this.loading = false;
    this.$emit('ready', true);
  }

  protected async create() {
    if (!this.current.title) {
      return;
    }

    const group = await this.createGroup(this.current);

    this.groups.push(group);
    this.sortGroups();

    this.stopCreating();

    this.selected = group;
    this.$nextTick(() => {
      this.scrollToSelected();
    })

    this.emitSelected();
  }

  protected canAdd(value: Entry): boolean {
    if (!this.selected) {
      return false;
    }
    return this.groupHasEntry(this.selected, value) === false;
  }

  protected async addEntry(value: Entry) {
    if (!this.selected) {
      return;
    }
    this.groupAddEntry(this.selected, value);

    try {
      this.loading = true;
      await this.addEntryToGroup(value, this.selected);
      this.loading = false;
      this.emitSelected();
    } catch (e) {
      this.groupRemoveEntry(this.selected, value);
    }
  }

  protected async removeEntry(value: Entry) {
    if (!this.selected) {
      return;
    }

    this.groupRemoveEntry(this.selected, value);

    try {
      this.loading = true;
      await this.removeEntryFromGroup(value, this.selected);
      this.loading = false;
      this.emitSelected();
    } catch (e) {
      this.groupAddEntry(this.selected, value);
    }
  }

  protected scrollToSelected() {
    if (this.selected === null) {
      return;
    }

    const container = this.$refs.list as HTMLElement;
    const selected = this.$refs[`item-${this.selected.id}`] as Element[];

    if (selected.length === 0) {
      return;
    }

    const element = selected[0] as HTMLElement;
    if (element.offsetTop > container.clientHeight) {
      container.scrollTop = (element.offsetTop + element.clientHeight) - container.clientHeight;
    } else if (element.offsetTop < container.scrollTop) {
      container.scrollTop = element.offsetTop;
    }
  }

  protected sortGroups() {
    this.groups.sort((first: EntryGroupItem, second: EntryGroupItem): number => {
      if (first.title.toUpperCase() < second.title.toUpperCase()) {
        return -1;
      } else if (first.title.toUpperCase() > second.title.toUpperCase()) {
        return 1;
      }

      return 0;
    });
  }

  protected clearSearchResult() {
    this.searchResult.items = [];
    this.searchResult.total = 0;
  }

  protected emitSelected() {
    this.$emit('selected', this.selected);
  }

  protected onSearch(results: VEntrySearchResult) {
    this.searchResult = results;
  }

  private groupHasEntry(group: EntryGroupItem, entry: Entry) {
    if (group.entries) {
      return group.entries.indexOf(entry) > -1;
    }
    return false;
  }

  private groupAddEntry(group: EntryGroupItem, entry: Entry) {
    if (group.entries) {
      group.entries.push(entry);
    } else {
      group.entries = [entry];
    }
  }

  private groupRemoveEntry(group: EntryGroupItem, entry: Entry) {
    if (group.entries) {
      const idx = group.entries.indexOf(entry);
      group.entries = group.entries.splice(idx, 1);
    }
  }
}
